import React, { Component } from 'react'
import { View, TextInput, Keyboard, Animated, Platform } from 'react-native'
import styled from 'styled-components/native'

import { Text } from '../../Atoms/Typography'
import PageHeader from '../../Molecules/PageHeader'
import InlineError from '../../Atoms/InlineError'
import TranslatedText from '../../Atoms/TranslatedText'
import { thousandSeparator, percentage } from '../../../lib/formatters'
import { translate } from '../../../services/translate'
import theme from '../../../styles/theme'
import { calculateForecastValues, calculateTotalOpeningBalance, calculateRiskWeightedAssets, calculateForecastDeductions } from './calculations'

const Container = styled(View)`
  flex: 1;
  flex-direction: column;
`

const Content = styled(Animated.View)`
  flex: 1;
  flex-direction: column;
  position: absolute;
  width: 100%;
  left: 0;
  z-index: -10;
`

const StyledInput = styled(TextInput)`
  height: 30px;
  width: 75px;
  border-color: ${props => props.editable ? theme.colours.grey : theme.colours.grey};
  background-color: ${props => props.editable ? theme.colours.blue : theme.colours.grey};
  border-width: 1px;
  border-radius: 5px;
  color: ${props => props.editable ? theme.colours.white : theme.colours.black}
`

const _Row = styled(View)`
  flex-direction: row;
  align-items: center;
  padding-left: ${2 * theme.gutterUnit}px;
  padding-right: ${2 * theme.gutterUnit}px;
`

const HeaderRow = styled(_Row)`
  background-color: ${theme.colours.grey};
  padding-top: ${theme.gutterUnit}px;
  padding-bottom: ${theme.gutterUnit}px;
  border-top-width: 1px;
  border-color: ${theme.colours.bordergrey};
  border-bottom-width: 1px;
  border-color: ${theme.colours.bordergrey};
`

const Row = styled(_Row)`
  padding-top: ${theme.gutterUnit / 2}px;
  padding-bottom: ${theme.gutterUnit / 2}px;
`

const LabelColumn = styled(View)`
  width: ${(100 / 7) * 2}%;
  justify-content: center;
`

const SpanColumn = styled(View)`
  width: ${props => (100 / 7) * props.span}%;
  justify-content: center;
`

const _Column = styled(View)`
  width: ${100 / 7}%;
`

const CenteredColumn = styled(_Column)`
  align-items: center;
`

const RightColumn = styled(_Column)`
  align-items: flex-end;
`

const RightCenteredColumn = styled(_Column)`
  align-items: flex-end;
  padding-right: ${props => (props.padding || 1) * theme.gutterUnit}px;
`

const Section = styled(View)`
  background-color: ${theme.colours.white};
  border-bottom-width: 1px;
  border-color: ${theme.colours.bordergrey};
  padding-top: ${theme.gutterUnit / 2}px;
  padding-bottom: ${theme.gutterUnit / 2}px;
`

const formatValue = (val, input = false) => {
  if (val === 0) {
    return '0'
  }
  if (Platform.OS === 'web' && input) {
    return `${val}`
  }
  if (val < 0) {
    return `(${thousandSeparator(val * -1)})`
  }
  return `${thousandSeparator(val)}`
}

const formatPercentage = (val, dp, hidePercentage) => {
  if (val === 0) {
    return '-'
  }
  return percentage(val, dp, hidePercentage)
}

const isDeduction = (val) => val === -1
const isAsset = val => val >= 0

export default class TreasuryPage3 extends Component {
  state = {
    error: null
  }

  constructor (props) {
    super(props)
    this.viewOffet = new Animated.Value(theme.teamBarHeight)
  }

  shouldComponentUpdate (nextProps, nextState) {
    if (!nextProps.active) {
      return false
    }
    return true
  }

  componentDidMount () {
    this.keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', this._keyboardDidShow)
    this.keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', this._keyboardDidHide)
  }

  componentWillUnmount () {
    this.keyboardDidShowListener.remove()
    this.keyboardDidHideListener.remove()
  }

  _keyboardDidShow = () => {
    if (!this.props.active) {
      return
    }
    this.setState({ error: null })
    this.tempValue = 0
    this.tempType = null

    if (this.shouldAvoidKeyboard) {
      Animated.parallel([
        Animated.timing(this.viewOffet, {
          duration: 500,
          toValue: theme.teamBarHeight - 250
        })
      ]).start()
    }
  }

  resetOffset = (skipAvoid = false) => {
    Animated.parallel([
      Animated.timing(this.viewOffet, {
        duration: 500,
        toValue: theme.teamBarHeight
      })
    ]).start()
    if (!skipAvoid) {
      Keyboard.dismiss()
    }
  }

  _keyboardDidHide = (skipAvoid = false) => {
    if (!this.props.active) {
      return
    }
    if (!skipAvoid) {
      this.shouldAvoidKeyboard = false
    }
    if (this.tempValue == null || typeof this.tempValue !== 'string') {
      this.resetOffset(skipAvoid)
      return
    }
    if (isNaN(this.tempValue) || parseInt(this.tempValue).toString() !== this.tempValue) {
      this.resetOffset(skipAvoid)
      return this.setState({ error: 'Input must be a positive whole number' })
    }
    const val = parseInt(this.tempValue) * 100

    const valuesToChange = {
      [this.tempType]: val
    }

    if (this.tempType.endsWith('Growth')) {
      const { totalForecast } = this.calculateAssetForecast(false, this.tempType, val)
      const totalOpeningBalance = this.calculateTotalOpeningBalance()
      const totalAssetGrowth = parseInt((((totalForecast / totalOpeningBalance) - 1) * 100).toFixed(0) * 100)
      valuesToChange.allOtherAssetsPercentage = totalAssetGrowth
    }

    this.resetOffset(skipAvoid)
    this.tempType = null
    this.tempValue = null
    this.props.onChangeValue(valuesToChange)
  }

  calculateTotalOpeningBalance = () => {
    const { treasury: { openingBalances } } = this.props
    return calculateTotalOpeningBalance(openingBalances)
  }

  calculateAssetForecast = (onlyAssets, type, val) => {
    const { treasury: { openingBalances }, decisions } = this.props
    const decisionsAndGrowth = { ...decisions }
    if (type) {
      decisionsAndGrowth[type] = val
    }
    return calculateForecastValues(onlyAssets, openingBalances, decisionsAndGrowth)
  }

  onInput = (type, val) => {
    this.tempValue = val
    this.tempType = type
  }

  onFocus = (type) => {
    this.setState({ error: null })
    if (type && this.tempType && type !== this.tempType) {
      this._keyboardDidHide(true)
    }
  }

  onBlur = (type) => {
    this._keyboardDidHide(true)
  }

  renderInput = (field, defaultValue, percentage, shouldAvoidKeyboard = false) => {
    return (
      <StyledInput
        key={`${field}Input${this.props.forceRerender}`}
        onChangeText={(val) => this.onInput(field, val)}
        defaultValue={percentage ? formatPercentage(defaultValue, 0, true) : formatValue(defaultValue, true)}
        autoCorrect={false}
        underlineColorAndroid='transparent'
        keyboardType='numeric'
        multiline={Platform.OS !== 'web'}
        onFocus={() => {
          this.shouldAvoidKeyboard = shouldAvoidKeyboard
          this.onFocus(field)
        }}
        onBlur={() => this.onBlur(field)}
        editable={this.props.enableChanges}
        style={{ textAlign: 'center', fontFamily: 'ubuntu-medium', fontSize: 18 }}
      />
    )
  }

  renderBusinessRow = (businessName, balance, growthName, growthValue, forecastBalance, riskName, riskValue, riskWeightedAssets) => {
    return (
      <Row>
        <LabelColumn><Text size='s'>{businessName}</Text></LabelColumn>
        <RightCenteredColumn padding={4}><Text size='s'>{formatValue(balance)}</Text></RightCenteredColumn>
        <CenteredColumn>{this.renderInput(growthName, growthValue / 100, false, riskName === null)}</CenteredColumn>
        <RightCenteredColumn padding={4}><Text size='s'>{formatValue(forecastBalance.toFixed(0))}</Text></RightCenteredColumn>
        <CenteredColumn>{riskName !== null ? this.renderInput(riskName, riskValue, true) : <Text> </Text>}</CenteredColumn>
        <RightCenteredColumn><Text size='s'>{formatValue(riskWeightedAssets.toFixed(0))}</Text></RightCenteredColumn>
      </Row>
    )
  }

  render () {
    const { decisions, treasury: { openingBalances, creditCardsEnabled, institutionalBankingEnabled, offshoreBankEnabled, wealthManagementEnabled } } = this.props
    const { homeLoansGrowth, businessLoansGrowth, creditCardsGrowth, institutionalBankingGrowth, offshoreBankGrowth, wealthGrowth, investmentsGrowth, fixedAssetsGrowth } = decisions

    const assetForecasts = this.calculateAssetForecast(false)
    const { homeLoansForecast, businessLoansForecast, creditCardsForecast, institutionalBankingForecast, offshoreBankForecast, wealthForecast, investmentsForecast, fixedAssetsForecast } = assetForecasts

    const {
      homeLoansRiskWeightedAsset,
      businessLoansRiskWeightedAsset,
      creditCardsRiskWeightedAsset,
      institutionalBankingRiskWeightedAsset,
      offshoreBankRiskWeightedAsset,
      wealthRiskWeightedAsset,
      investmentsRiskWeightedAsset,
      fixedAssetsRiskWeightedAsset,
      totalCreditRiskWeightedAsset,
      operationalRiskWeightedAsset,
      totalRiskWeightedAsset
    } = calculateRiskWeightedAssets(assetForecasts, decisions)

    const {
      homeLoansForecastDeduction,
      businessLoansForecastDeduction,
      creditCardsForecastDeduction,
      institutionalBankingForecastDeduction,
      offshoreBankForecastDeduction,
      wealthForecastDeduction,
      investmentsForecastDeduction,
      fixedAssetsForecastDeduction,
      totalCapitalDeductions
    } = calculateForecastDeductions(assetForecasts, decisions)

    return (
      <Container>
        <PageHeader text={translate('treasury.screen3.screenHeader', 'FORECAST RISK WEIGHTED ASSETS & CAPITAL DEDUCTIONS').toUpperCase()} />
        {this.state.error && (
          <InlineError message={this.state.error} />
        )}
        <Content style={{ top: this.viewOffet }}>
          <HeaderRow>
            <LabelColumn><TranslatedText medium size='s' uppercase value='treasury.screen3.table1Column1Header' default='FORECAST RISK WEIGHTED ASSETS' /></LabelColumn>
            <CenteredColumn><TranslatedText medium size='s' center uppercase value='treasury.screen3.table1Column2Header' default={'CURRENT\nBALANCE'} /></CenteredColumn>
            <CenteredColumn><Text medium size='s' center uppercase>{`${translate('treasury.screen3.table1Column3Header', 'GROWTH')}\n${translate('general.percentage', '%')}`}</Text></CenteredColumn>
            <CenteredColumn><Text medium size='s' center uppercase>{`${translate('treasury.screen3.table1Column4Header', 'FORECAST\nBALANCE')} *`}</Text></CenteredColumn>
            <CenteredColumn><Text medium size='s' center uppercase>{`${translate('treasury.screen3.table1Column5Header', 'RISK WEIGHT')}\n${translate('general.percentage', '%')}`}</Text></CenteredColumn>
            <RightColumn><Text medium size='s' right uppercase>{`${translate('treasury.screen3.table1Column6Header', 'RISK WEIGHTED\nASSETS')} *`}</Text></RightColumn>
          </HeaderRow>
          <Section>
            {isAsset(decisions.homeLoansRisk) && this.renderBusinessRow('MORTGAGES', openingBalances.homeLoans, 'homeLoansGrowth', homeLoansGrowth, homeLoansForecast, 'homeLoansRisk', decisions.homeLoansRisk, homeLoansRiskWeightedAsset)}
            {isAsset(decisions.businessLoansRisk) && this.renderBusinessRow('BUSINESS LENDING', openingBalances.businessLoans, 'businessLoansGrowth', businessLoansGrowth, businessLoansForecast, 'businessLoansRisk', decisions.businessLoansRisk, businessLoansRiskWeightedAsset)}
            {creditCardsEnabled && isAsset(decisions.creditCardsRisk) && this.renderBusinessRow('CREDIT CARDS', openingBalances.creditCards, 'creditCardsGrowth', creditCardsGrowth, creditCardsForecast, 'creditCardsRisk', decisions.creditCardsRisk, creditCardsRiskWeightedAsset)}
            {institutionalBankingEnabled && isAsset(decisions.institutionalBankingRisk) && this.renderBusinessRow('INSTITUTIONAL LENDING', openingBalances.institutionalBanking, 'institutionalBankingGrowth', institutionalBankingGrowth, institutionalBankingForecast, 'institutionalBankingRisk', decisions.institutionalBankingRisk, institutionalBankingRiskWeightedAsset)}
            {offshoreBankEnabled && isAsset(decisions.offshoreBankRisk) && this.renderBusinessRow('OFF SHORE BANK', openingBalances.offshoreBank, 'offshoreBankGrowth', offshoreBankGrowth, offshoreBankForecast, 'offshoreBankRisk', decisions.offshoreBankRisk, offshoreBankRiskWeightedAsset)}
            {wealthManagementEnabled && isAsset(decisions.wealthRisk) && this.renderBusinessRow('WEALTH MANAGEMENT', openingBalances.wealthAssets, 'wealthGrowth', wealthGrowth, wealthForecast, 'wealthRisk', decisions.wealthRisk, wealthRiskWeightedAsset)}
            {isAsset(decisions.investmentsRisk) && this.renderBusinessRow('INVESTMENTS', openingBalances.investments, 'investmentsGrowth', investmentsGrowth, investmentsForecast, 'investmentsRisk', decisions.investmentsRisk, investmentsRiskWeightedAsset)}
            {isAsset(decisions.fixedAssetsRisk) && this.renderBusinessRow('OTHER FIXED ASSETS', openingBalances.fixedAssets, 'fixedAssetsGrowth', fixedAssetsGrowth, fixedAssetsForecast, 'fixedAssetsRisk', decisions.fixedAssetsRisk, fixedAssetsRiskWeightedAsset)}
          </Section>
          <Section>
            <Row>
              <SpanColumn span={6}><Text medium size='s'>{`${translate('treasury.screen3.table1Total1Label', 'Total Credit Risk Weighted Assets')} *`}</Text></SpanColumn>
              <RightCenteredColumn><Text size='s'>{formatValue(totalCreditRiskWeightedAsset.toFixed(0))}</Text></RightCenteredColumn>
            </Row>
            <Row>
              <SpanColumn span={5}><Text size='s'>{`${translate('treasury.screen3.table1Total2Label', 'Operational Risk Assets (as % of Total Credit RWA)')} *`}</Text></SpanColumn>
              <CenteredColumn>{this.renderInput('opexRisk', decisions.opexRisk, true, true)}</CenteredColumn>
              <RightCenteredColumn><Text size='s'>{formatValue(operationalRiskWeightedAsset.toFixed(0))}</Text></RightCenteredColumn>
            </Row>
            <Row>
              <SpanColumn span={6}><Text medium size='s' primary uppercase>{`${translate('treasury.screen3.table1Total3Label', 'TOTAL RISK WEIGHTED ASSETS ( ESTIMATED )')} *`}</Text></SpanColumn>
              <RightCenteredColumn><Text size='s' primary>{formatValue(totalRiskWeightedAsset.toFixed(0))}</Text></RightCenteredColumn>
            </Row>
          </Section>
          <HeaderRow style={{ borderTopWidth: 0 }}>
            <LabelColumn><TranslatedText medium size='s' uppercase value='treasury.screen3.table2Column1Header' default='FORECAST CAPITAL DEDUCTIONS' /></LabelColumn>
            <CenteredColumn><TranslatedText medium size='s' center uppercase value='treasury.screen3.table2Column2Header' default={'CURRENT\nBALANCE'} /></CenteredColumn>
            <CenteredColumn><Text medium size='s' center uppercase>{`${translate('treasury.screen3.table2Column3Header', 'GROWTH')}\n${translate('general.percentage', '%')}`}</Text></CenteredColumn>
            <CenteredColumn><Text medium size='s' center uppercase>{`${translate('treasury.screen3.table2Column4Header', 'FORECAST\nBALANCE')} *`}</Text></CenteredColumn>
            <CenteredColumn><Text medium size='s'> </Text></CenteredColumn>
            <RightColumn><TranslatedText medium size='s' right uppercase value='treasury.screen3.table2Column5Header' default={'FORECAST\nDEDUCTIONS'} /></RightColumn>
          </HeaderRow>
          <Section>
            {isDeduction(decisions.homeLoansRisk) && this.renderBusinessRow('MORTGAGES', openingBalances.homeLoans, 'homeLoansGrowth', homeLoansGrowth, homeLoansForecast, null, null, homeLoansForecastDeduction)}
            {isDeduction(decisions.businessLoansRisk) && this.renderBusinessRow('BUSINESS LENDING', openingBalances.businessLoans, 'businessLoansGrowth', businessLoansGrowth, businessLoansForecast, null, null, businessLoansForecastDeduction)}
            {creditCardsEnabled && isDeduction(decisions.creditCardsRisk) && this.renderBusinessRow('CREDIT CARDS', openingBalances.creditCards, 'creditCardsGrowth', creditCardsGrowth, creditCardsForecast, null, null, creditCardsForecastDeduction)}
            {institutionalBankingEnabled && isDeduction(decisions.institutionalBankingRisk) && this.renderBusinessRow('INSTITUTIONAL LENDING', openingBalances.institutionalBanking, 'institutionalBankingGrowth', institutionalBankingGrowth, institutionalBankingForecast, null, null, institutionalBankingForecastDeduction)}
            {offshoreBankEnabled && isDeduction(decisions.offshoreBankRisk) && this.renderBusinessRow('OFF SHORE BANK', openingBalances.offshoreBank, 'offshoreBankGrowth', offshoreBankGrowth, offshoreBankForecast, null, null, offshoreBankForecastDeduction)}
            {wealthManagementEnabled && isDeduction(decisions.wealthRisk) && this.renderBusinessRow('WEALTH MANAGEMENT', openingBalances.wealthAssets, 'wealthGrowth', wealthGrowth, wealthForecast, null, null, wealthForecastDeduction)}
            {isDeduction(decisions.investmentsRisk) && this.renderBusinessRow('INVESTMENTS', openingBalances.investments, 'investmentsGrowth', investmentsGrowth, investmentsForecast, null, null, investmentsForecastDeduction)}
            {isDeduction(decisions.fixedAssetsRisk) && this.renderBusinessRow('OTHER FIXED ASSETS', openingBalances.fixedAssets, 'fixedAssetsGrowth', fixedAssetsGrowth, fixedAssetsForecast, null, null, fixedAssetsForecastDeduction)}
          </Section>
          <Section>
            <Row>
              <SpanColumn span={6}><Text medium size='s' primary uppercase>{`${translate('treasury.screen3.table2Total1Label', 'TOTAL CAPITAL DEDUCTIONS')} *`}</Text></SpanColumn>
              <RightCenteredColumn><Text size='s' primary>{formatValue(totalCapitalDeductions.toFixed(0))}</Text></RightCenteredColumn>
            </Row>
          </Section>
          <Row><Text size='s'>{`* ${translate('treasury.screen3.table2FootNote', 'indicates estimates only which are subject to actual performance')}`}</Text></Row>
        </Content>
      </Container>
    )
  }
}
